import SearchIcon from "@mui/icons-material/Search";
import { InputAdornment } from "@mui/material";
import * as React from "react";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useGetLanguages } from "../../../../api/services/client/clientApis";
import {
  Column,
  FormController,
  MuiBox,
  MuiGrid,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
} from "../../../../components";
import { ChipOptionsDataType } from "../../../../components/chip/MuiChip";
import MuiChipWithPopover from "../../../../components/chipWithPopover/MuiChipWithPopover";
import MuiEllipseLabel from "../../../../components/ellipseLabel/MuiEllipseLabel";
import { MuiLink } from "../../../../components/link/MuiLink";
import { PAGE_IDS } from "../../../../helpers/constants";
import { COMPONENT_IDS } from "../../../../helpers/constants/componentIds";
import { MAX_LENGTH_50 } from "../../../../helpers/constants/validations";
import { LabelValue } from "../../../../helpers/types/common";
import { getValueOrBlank, isNotEmpty } from "../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../hooks/useSiteCore";
import {
  ClientSpecificContent,
  ClientSpecificContentFormKey,
  Country,
  ProgramTemplate,
  Resource,
} from "../../modelsAndSchemas/Models";
import {
  clientSpecificContentKeys,
  createClientSiteCoreKeys,
} from "../../siteCore/SiteCoreKeys";
import { ClientSpecificContentListWrapper } from "./ClientSpecificContentList.style";
import { OptionMenu } from "./optionMenu/OptionMenu";

export const pageId = PAGE_IDS.CREATE_CLIENT_CLIENT_SPECIFIC_CONTENT_LIST;

export interface ClientSpecificContentFormProps {
  rows: ClientSpecificContent[];
  isEditable?: boolean;
  onClientSpecificContentEdit: (row: ClientSpecificContent) => void;
  onClientSpecificContentView: (row: ClientSpecificContent) => void;
  onClientSpecificContentDelete: (row: ClientSpecificContent) => void;
}

export const ClientSpecificContentList: FC<ClientSpecificContentFormProps> = ({
  rows,
  isEditable = true,
  onClientSpecificContentEdit,
  onClientSpecificContentView,
  onClientSpecificContentDelete,
}) => {
  const {
    fetchLabel,
    commonLabels,
    fetchDropdownValues,
    fetchValueByKeyOfDropdown,
  } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const tableRef = React.useRef<{ resetPagination: () => void }>(null);
  const [filteredResults, setFilteredResults] = useState<
    ClientSpecificContent[]
  >([...rows]);

  const [uniqueSolutionOfferings, setUniqueSolutionOfferings] = useState<
    LabelValue[]
  >([]);
  const [uniqueProgramClass, setUniqueProgramClass] = useState<LabelValue[]>(
    [],
  );

  const { data: languagesApiData } = useGetLanguages();

  const gridRef = useRef<HTMLDivElement>(null); // Create a reference for the MuiGrid

  const resetPagination = () => {
    if (tableRef.current) {
      tableRef.current.resetPagination();
    }
  };

  const getLabelFromClientSpecificContent = (key: string) => {
    return fetchLabel(createClientSiteCoreKeys.clientSpecificContentTitle, key);
  };

  const getLanguageDropdownData = useMemo(() => {
    return languagesApiData?.map((language: string) => ({
      label: language,
      value: language,
    }));
  }, [languagesApiData]);

  const { control, watch, getValues, reset } = useForm({
    mode: "all",
    defaultValues: {
      [ClientSpecificContentFormKey.clientResourceGroupName]: "",
      [ClientSpecificContentFormKey.resourceName]: "",
      [ClientSpecificContentFormKey.resourceTypeId]: "",
      [ClientSpecificContentFormKey.resourceId]: "",
      [ClientSpecificContentFormKey.languageId]: "",
      [ClientSpecificContentFormKey.programTemplateName]: "",
      [ClientSpecificContentFormKey.solutionOfferingsName]: "",
      [ClientSpecificContentFormKey.programClassName]: "",
      [ClientSpecificContentFormKey.country]: "",
    },
  });

  useEffect(() => {
    resetPagination();

    const myData = [...rows];

    // Extract unique solution categories and solution category LOBs
    const uniqueSolutionOfferings: LabelValue[] = Array.from(
      new Set(
        myData.flatMap((item) =>
          item.programTemplates.map(
            (template) => template.solutionOfferingsName,
          ),
        ),
      ),
    ).map((solutionOfferingsName) => ({
      value: solutionOfferingsName,
      label: solutionOfferingsName,
    }));
    const uniqueProgramClass: LabelValue[] = Array.from(
      new Set(
        myData.flatMap((item) =>
          item.programTemplates.map((template) => template.programClassName),
        ),
      ),
    ).map((programClass) => ({ value: programClass, label: programClass }));

    setUniqueSolutionOfferings(uniqueSolutionOfferings);
    setUniqueProgramClass(uniqueProgramClass);
    reset();
  }, [rows]);

  useEffect(() => {
    const {
      clientResourceGroupName,
      resourceName,
      resourceTypeId,
      resourceId,
      languageId,
      programTemplateName,
      solutionOfferingsName,
      programClassName,
      country,
    } = getValues();

    const myData = [...rows].filter((item) => {
      return (
        item.clientResourceGroupName
          .toString()
          .toLowerCase()
          .includes(clientResourceGroupName.toLowerCase()) &&
        item.resources.some((resource) =>
          resource.resourceName
            .toLowerCase()
            .includes(resourceName.toLowerCase()),
        ) &&
        item.resources.some((resource) =>
          resource.resourceTypeId
            .toLowerCase()
            .includes(resourceTypeId.toLowerCase()),
        ) &&
        item.resources.some((resource) =>
          resource.resourceId.toLowerCase().includes(resourceId.toLowerCase()),
        ) &&
        item.resources.some((resource) =>
          resource.languageId.toLowerCase().includes(languageId.toLowerCase()),
        ) &&
        item.programTemplates.some((programTemplateObj) =>
          programTemplateObj.programTemplateName
            .toLowerCase()
            .includes(programTemplateName.toLowerCase()),
        ) &&
        item.programTemplates.some((programTemplateObj) =>
          programTemplateObj.solutionOfferingsName
            .toLowerCase()
            .includes(solutionOfferingsName.toLowerCase()),
        ) &&
        item.programTemplates.some((programTemplateObj) =>
          programTemplateObj.programClassName
            ? programTemplateObj?.programClassName
                ?.toLowerCase()
                .includes(programClassName.toLowerCase())
            : true,
        ) &&
        item.programTemplates.some((programTemplateObj) => {
          let recordFound = false;
          let countriesAsString = "";
          if (isNotEmpty(programTemplateObj.countries)) {
            programTemplateObj.countries.forEach((countryObj) => {
              countriesAsString += countryObj.countryName + ", ";
              if (
                countryObj.countryName
                  .toLowerCase()
                  .includes(country?.toLowerCase())
              ) {
                recordFound = true;
              }
            });
          }
          programTemplateObj.countriesAsString = countriesAsString;
          if (getValueOrBlank(country).length === 0) {
            recordFound = true;
          }
          return recordFound;
        })
      );
    });

    resetPagination();
    setFilteredResults([...myData]);
  }, [
    rows,
    watch(ClientSpecificContentFormKey.clientResourceGroupName),
    watch(ClientSpecificContentFormKey.resourceName),
    watch(ClientSpecificContentFormKey.resourceTypeId),
    watch(ClientSpecificContentFormKey.resourceId),
    watch(ClientSpecificContentFormKey.languageId),
    watch(ClientSpecificContentFormKey.programTemplateName),
    watch(ClientSpecificContentFormKey.solutionOfferingsName),
    watch(ClientSpecificContentFormKey.programClassName),
    watch(ClientSpecificContentFormKey.country),
  ]);

  const deleteClientSpecificContentAction = (row: ClientSpecificContent) => {
    onClientSpecificContentDelete(row);
  };

  const columns: Column[] = [
    {
      id: ClientSpecificContentFormKey.clientResourceGroupName,
      label: getLabelFromClientSpecificContent(
        clientSpecificContentKeys.groupName,
      ),
      render: (row: any) => (
        <MuiLink
          data-testid={`${COMPONENT_IDS.COMMON_VIEW_LINK}-${row.clientResourceGroupId}`}
          onClick={() =>
            onClientSpecificContentView?.(
              row as unknown as ClientSpecificContent,
            )
          }
        >
          <MuiBox>
            <MuiEllipseLabel
              labelTypoClassName="Link ellipsis-with-tooltip"
              text={row.clientResourceGroupName}
              labelTypoVariant="h3"
              placement="bottom-start"
            />
          </MuiBox>
        </MuiLink>
      ),
      isSortVisible: true,
      className: "cell-group-id",
    },
    {
      id: ClientSpecificContentFormKey.resourceName,
      objectKeyName: "resources",
      label: getLabelFromClientSpecificContent(
        createClientSiteCoreKeys.resourceName,
      ),
      isSortVisible: true,
      className: "cell-200-width",
      render: (row: any) => (
        <MuiBox>
          {row.resources.map((resource: Resource, index: number) => {
            const className = index > 0 ? "nestedElement" : "";
            return (
              <MuiBox
                className={className}
                key={`${resource.resourceName}-${index}`}
              >
                <MuiEllipseLabel
                  labelTypoClassName="Link ellipsis-with-tooltip"
                  key={`${resource.resourceName}-${index}`}
                  text={resource.resourceName}
                  labelTypoVariant="h3"
                  placement="bottom-start"
                />
              </MuiBox>
            );
          })}
        </MuiBox>
      ),
    },
    {
      id: ClientSpecificContentFormKey.resourceTypeId,
      objectKeyName: "resources",
      label: getLabelFromClientSpecificContent(
        createClientSiteCoreKeys.resourceType,
      ),
      className: "cell-200-width",
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3">
            {row.resources.map((resource: Resource, index: number) => {
              const className = index > 0 ? "nestedElement" : "";
              return (
                <MuiTypography
                  className={className}
                  variant="h3"
                  key={`${resource.resourceTypeId}-${index}`}
                >
                  {fetchValueByKeyOfDropdown(
                    createClientSiteCoreKeys.clientSpecificContentTitle,
                    createClientSiteCoreKeys.resourceTypeList,
                    resource.resourceTypeId,
                  )}
                </MuiTypography>
              );
            })}
          </MuiTypography>
        </MuiBox>
      ),
      isSortVisible: true,
    },
    {
      id: ClientSpecificContentFormKey.resourceId,
      objectKeyName: "resources",
      label: getLabelFromClientSpecificContent(
        createClientSiteCoreKeys.resourceID,
      ),
      isSortVisible: true,
      className: "cell-200-width",
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3">
            {row.resources.map((resource: Resource, index: number) => {
              const className = index > 0 ? "nestedElement" : "";
              return (
                <MuiBox
                  className={className}
                  key={`${resource.resourceId}-${index}`}
                >
                  <MuiEllipseLabel
                    labelTypoClassName="Link ellipsis-with-tooltip"
                    key={`${resource.resourceId}-${index}`}
                    text={resource.resourceId}
                    labelTypoVariant="h3"
                    placement="bottom-start"
                  />
                </MuiBox>
              );
            })}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: ClientSpecificContentFormKey.languageId,
      objectKeyName: "resources",
      label: getLabelFromClientSpecificContent(createClientSiteCoreKeys.locale),
      render: (row: any) => (
        <MuiBox>
          {row.resources.map((resource: Resource, index: number) => {
            const className = index > 0 ? "nestedElement" : "";
            return (
              <MuiTypography
                className={className}
                variant="h3"
                key={`${resource.languageId}-${index}`}
              >
                {resource.languageId}
              </MuiTypography>
            );
          })}
        </MuiBox>
      ),
      isSortVisible: true,
      className: "cell-locale",
    },
    {
      id: ClientSpecificContentFormKey.programTemplateName,
      objectKeyName: "programTemplates",
      label: getLabelFromClientSpecificContent(
        createClientSiteCoreKeys.programTemplate,
      ),
      isSortVisible: true,
      render: (row: any) => (
        <MuiBox>
          {row.programTemplates.map(
            (programTemplate: ProgramTemplate, index: number) => {
              const className = index > 0 ? "nestedElement" : "";
              return (
                <MuiTypography
                  className={className}
                  variant="h3"
                  key={`${programTemplate.programTemplateName}-${index}`}
                >
                  {programTemplate.programTemplateName}
                </MuiTypography>
              );
            },
          )}
        </MuiBox>
      ),
      className: "cell-250-width",
    },
    {
      id: ClientSpecificContentFormKey.solutionOfferingsName,
      objectKeyName: "programTemplates",
      label: getLabelFromClientSpecificContent(
        createClientSiteCoreKeys.solutionOfferings,
      ),
      isSortVisible: true,
      className: "cell-200-width",
      render: (row: any) => (
        <MuiBox>
          {row.programTemplates.map(
            (programTemplate: ProgramTemplate, index: number) => {
              const className = index > 0 ? "nestedElement" : "";
              return (
                <MuiTypography
                  className={className}
                  variant="h3"
                  key={`${programTemplate.solutionOfferingsName}-${index}`}
                >
                  {programTemplate.solutionOfferingsName}
                </MuiTypography>
              );
            },
          )}
        </MuiBox>
      ),
    },
    {
      id: ClientSpecificContentFormKey.programClassName,
      objectKeyName: "programTemplates",
      label: getLabelFromClientSpecificContent(
        createClientSiteCoreKeys.programClass,
      ),
      isSortVisible: true,
      className: "cell-200-width",
      render: (row: any) => (
        <MuiBox>
          {row.programTemplates.map(
            (programTemplate: ProgramTemplate, index: number) => {
              const className = index > 0 ? "nestedElement" : "";
              return (
                <MuiTypography
                  className={className}
                  variant="h3"
                  key={`${programTemplate.programClassName}-${index}`}
                >
                  {programTemplate.programClassName}
                </MuiTypography>
              );
            },
          )}
        </MuiBox>
      ),
    },
    {
      id: ClientSpecificContentFormKey.countriesAsString,
      objectKeyName: "programTemplates",
      label: getLabelFromClientSpecificContent(
        createClientSiteCoreKeys.countryClientSpecific,
      ),
      isSortVisible: true,
      className: "cell-country",
      render: (row: any) => {
        return (
          <MuiBox ref={gridRef}>
            {row.programTemplates.map(
              (programTemplate: ProgramTemplate, index: number) => {
                const className = index > 0 ? "nestedElement" : "";

                const selectedCountries: ChipOptionsDataType[] =
                  programTemplate.countries.map((country: Country) => ({
                    label: country.countryName,
                    value: country.countryName,
                  }));

                return (
                  <MuiBox
                    key={`country_index${selectedCountries[index]?.label ?? ""}`}
                    className={className}
                  >
                    <MuiChipWithPopover
                      data={selectedCountries}
                      handleDelete={() => {}}
                      id={"selected-countries"}
                      isEditable={false}
                      headerLabel={commonLabels.selectedCountries}
                      gridRef={gridRef}
                      showMoreARIALabel={commonARIALabel.showMoreDetails}
                      removeChipNameARIALabel={commonARIALabel.removeChipName}
                      closePopoverARIALabel={commonARIALabel.closePopover}
                    />
                  </MuiBox>
                );
              },
            )}
          </MuiBox>
        );
      },
    },
    {
      id: "action",
      label: getLabelFromClientSpecificContent(
        createClientSiteCoreKeys.actionClientSpecific,
      ),
      render: (row: any) =>
        isEditable ? (
          <MuiBox>
            <OptionMenu
              clientData={row}
              onClickOptionEdit={onClientSpecificContentEdit}
              onClickOptionDelete={deleteClientSpecificContentAction}
            />
          </MuiBox>
        ) : null,
    },
  ];

  const renderSearchIcon = () => (
    <InputAdornment position="start">
      <SearchIcon />
    </InputAdornment>
  );

  const tableHeaderForm = [
    <FormController
      key={ClientSpecificContentFormKey.clientResourceGroupName}
      control={control}
      name={ClientSpecificContentFormKey.clientResourceGroupName}
    >
      <MuiTextField
        id={`${pageId}-${ClientSpecificContentFormKey.clientResourceGroupName}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        showCloseIcon={true}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ClientSpecificContentFormKey.resourceName}
      key={ClientSpecificContentFormKey.resourceName}
    >
      <MuiTextField
        id={`${pageId}-${ClientSpecificContentFormKey.resourceName}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        showCloseIcon={true}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ClientSpecificContentFormKey.resourceTypeId}
      key={ClientSpecificContentFormKey.resourceTypeId}
    >
      <MuiSelect
        visibleDefaultSelectOption
        label=""
        options={fetchDropdownValues(
          createClientSiteCoreKeys.clientSpecificContentTitle,
          createClientSiteCoreKeys.resourceTypeList,
          true,
        )}
        value=""
        id={`${pageId}-${ClientSpecificContentFormKey.resourceTypeId}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ClientSpecificContentFormKey.resourceId}
      key={ClientSpecificContentFormKey.resourceId}
    >
      <MuiTextField
        id={`${pageId}-${ClientSpecificContentFormKey.resourceId}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        showCloseIcon={true}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ClientSpecificContentFormKey.languageId}
      key={ClientSpecificContentFormKey.languageId}
    >
      <MuiSelect
        visibleDefaultSelectOption
        label=""
        options={getLanguageDropdownData ?? []}
        value=""
        id={`${pageId}-${ClientSpecificContentFormKey.languageId}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ClientSpecificContentFormKey.programTemplateName}
      key={ClientSpecificContentFormKey.programTemplateName}
    >
      <MuiTextField
        id={`${pageId}-${ClientSpecificContentFormKey.programTemplateName}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        showCloseIcon={true}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ClientSpecificContentFormKey.solutionOfferingsName}
      key={ClientSpecificContentFormKey.solutionOfferingsName}
    >
      <MuiSelect
        visibleDefaultSelectOption
        label=""
        options={uniqueSolutionOfferings}
        value=""
        id={`${pageId}-${ClientSpecificContentFormKey.solutionOfferingsName}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ClientSpecificContentFormKey.programClassName}
      key={ClientSpecificContentFormKey.programClassName}
    >
      <MuiSelect
        visibleDefaultSelectOption
        label=""
        options={uniqueProgramClass}
        value=""
        id={`${pageId}-${ClientSpecificContentFormKey.programClassName}`}
        placeholder={commonLabels.selectPlaceHolder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    <FormController
      control={control}
      name={ClientSpecificContentFormKey.country}
      key={ClientSpecificContentFormKey.country}
    >
      <MuiTextField
        id={`${pageId}-${ClientSpecificContentFormKey.country}`}
        startAdornment={renderSearchIcon()}
        aria-label={commonLabels.search}
        showCloseIcon={true}
        maxLength={MAX_LENGTH_50}
        placeholder={commonLabels.search}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,

    <></>,
  ];

  return (
    <ClientSpecificContentListWrapper>
      <MuiGrid container>
        <MuiGrid size={{ xs: 12 }} className="header-right">
          <MuiTable
            ref={tableRef}
            pageId={pageId}
            tableHeaderForm={tableHeaderForm}
            columns={columns}
            isLastColumnSticky={isEditable}
            tableResults={[...filteredResults]}
            noDataInApi={rows.length === 0}
            noResultMessage={commonLabels.noRecordsFound}
            emptyMessage={getLabelFromClientSpecificContent(
              clientSpecificContentKeys.noRecordsAvailable,
            )}
            isGroupView
            priorityResults={[]}
          />
        </MuiGrid>
      </MuiGrid>
    </ClientSpecificContentListWrapper>
  );
};
